import { Info as InfoIcon } from '@material-ui/icons';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CustomTable, EmptyState, InfoPointIcon, MobileTable, palette, ThrottleDot, Tooltip, Typography } from '@dev/ee-components';
import { ThrottlingStatus } from '@dev/ee-api/ee-api-models';
import { Grid } from '@material-ui/core';

import { getLogDomainsTotalCount, getLogsInProgressDomains, getLogInProgressDomainsLoading } from '@api/store/logs/actions';

import { useMobile } from '@api/services/hooks/useMobile';

const throttleColors = {
    [ThrottlingStatus.Red]: palette.red[6],
    [ThrottlingStatus.Yellow]: palette.orange[5],
    [ThrottlingStatus.Green]: palette.teal[5],
};

const EmptyStateWrapper = styled.div`
    margin-top: 16px;
    & > div {
        padding: 16px;
    }
`;

const TableContainer = styled.div`
    border-bottom: 1px solid ${palette.gray[3]};
    tr > td:nth-last-child(2) .hideCustomTable {
        justify-content: flex-end;
    }
    .MuiTableRow-head > th:nth-child(2) > span {
        padding: 0;
    }
`;

const OverviewContainer = styled.div<{ isMobile: boolean }>`
    border: 1px solid ${palette.gray[3]};
    border-radius: 4px;
    margin-top: 24px;
    padding: ${(props): string => (props.isMobile ? '32px 20px 0px' : '32px 0 0 0')};
    background-color: #fff;
`;

const Info = styled.div<{ isMobile: boolean }>`
    display: flex;
    margin: ${(props): string => (props.isMobile ? '0 0 36px 0' : '0px 48px 32px')};
    border-radius: 4px;
    padding: 16px;
    background-color: ${palette.indigo[1]};
`;

const InfoIconContainer = styled.div`
    margin-right: 16px;
    padding-top: 4px;
`;

const StyledTooltip = styled.div<{ isMobile: boolean }>`
    & .MuiTypography-h5 {
        min-width: 150px;
    }

    ${({ isMobile }) => (isMobile ? `margin-left: 8px;` : `margin-right:4px;`)}

    svg {
        fill: ${palette.gray[4]};
    }
    text-align: left;
    display: flex;
`;

const tooltip = (id: string, title: string, isMobile?: boolean): JSX.Element => {
    return (
        <StyledTooltip isMobile={isMobile}>
            <Tooltip id={id} title={title} color={'#fff'} interactive PopperProps={{ keepMounted: false, disablePortal: true }}>
                <InfoIcon fontSize="small" color="inherit" />
            </Tooltip>
        </StyledTooltip>
    );
};

const getTableRows = (domains: any): any[] => {
    if (!domains) {
        return [];
    }

    return mapInprogressStas(domains);
};

const mapInprogressStas = (domains: any) => {
    return domains?.map((domain) => {
        const selectedThrottleColor = throttleColors[domain?.ThrottlingStatus] || palette.teal[5];

        return {
            domain: domain.Domain,
            inprogress: domain.Count,
            waiting: domain.Count - domain.Ready,
            readytosend: domain.Ready,
            attemptrate: `${domain.Rate.toFixed(1)} /s`,
            successrate: `${domain.SentRate.toFixed(1)} /s`,
            throttling: (
                <Grid container justifyContent="flex-end">
                    <ThrottleDot color={selectedThrottleColor} />
                </Grid>
            ),
        };
    });
};

const InProgressOverviewMobileTable: FC = (): JSX.Element => {
    const { t } = useTranslation();
    const totalCount = useSelector(getLogDomainsTotalCount);
    const domains = useSelector(getLogsInProgressDomains);

    if (!totalCount) {
        return (
            <EmptyStateWrapper>
                <EmptyState background="#fff" text={t('thereisnodatatodisplay')} />
            </EmptyStateWrapper>
        );
    }

    return (
        <>
            <MobileTable
                headers={
                    [
                        t('domain'),
                        t('inprogress'),
                        t('waiting'),
                        t('readytosend'),
                        t('attemptrate'),
                        <Grid key="successRate" container alignItems="center">
                            {t('successrate')}
                            {tooltip('successrate', t('successrate_tooltip'), true)}
                        </Grid>,
                        <Grid key="throttling" container alignItems="center">
                            {t('throttling')} {tooltip('successrate', t('throttling_tooltip'), true)}
                        </Grid>,
                    ] as any
                }
                rows={getTableRows(domains)}
                footer={<></>}
            />
        </>
    );
};

const InProgressOverviewDesktopTable: FC = (): JSX.Element => {
    const { t } = useTranslation();
    const totalCount = useSelector(getLogDomainsTotalCount);
    const domains = useSelector(getLogsInProgressDomains);
    const isLoading = useSelector(getLogInProgressDomainsLoading);

    const SkeletonRow = [
        {
            domain: <Skeleton />,
            inprogress: <Skeleton />,
            waiting: <Skeleton />,
            readytosend: <Skeleton />,
            attemptrate: <Skeleton />,
            successrate: <Skeleton />,
            throttling: <Skeleton />,
        },
    ];

    return (
        <TableContainer>
            <CustomTable
                hideCheckboxes
                disableRowHover
                disableFirstRowBorderTop
                rows={isLoading && !totalCount ? SkeletonRow : getTableRows(domains)}
                headers={
                    [
                        { id: 'domain', numeric: false, disablePadding: true, columnAlign: 'left', label: t('domain') },
                        { id: 'inprogress', numeric: true, disablePadding: false, columnAlign: 'right', label: t('inprogress') },
                        { id: 'waiting', numeric: true, disablePadding: false, columnAlign: 'right', label: t('waiting') },
                        { id: 'readytosend', numeric: true, disablePadding: false, columnAlign: 'right', label: t('readytosend') },
                        { id: 'attemptrate', numeric: true, disablePadding: false, columnAlign: 'right', label: t('attemptrate') },
                        {
                            id: 'successrate',
                            numeric: true,
                            disablePadding: false,
                            columnAlign: 'right',
                            label: (
                                <>
                                    {' '}
                                    {t('successrate')}
                                    {tooltip('successrate', t('successrate_tooltip'))}
                                </>
                            ),
                        },
                        {
                            id: 'throtlling',
                            numeric: true,
                            disablePadding: false,
                            columnAlign: 'right',
                            label: (
                                <>
                                    {' '}
                                    {t('throttling')}
                                    {tooltip('successrate', t('throttling_tooltip'))}
                                </>
                            ),
                        },
                    ] as any
                }
                isEmpty={!isLoading && !totalCount}
                emptyStateProps={{ text: t('thereisnodatatodisplay') }}
            />
        </TableContainer>
    );
};

/**
 * As for now domains are not supporing paging via api request call this component can be used when it will be seperated to diffrent call [PP]

const InProgressOverviewFooter: FC = (): JSX.Element => {
    const totalCount = useSelector(getLogDomainsTotalCount)
    const isLoading = useSelector(getLogInProgressDomainsLoading);
    const activePage = useSelector(getLogsInProgressActivePage)
    const { t } = useTranslation();
    const isMobile = useMobile();
    const dispatch = useDispatch();
    
    if (!totalCount) {
        return (<></>)
    }

    const perPage = 12;
    const offset = totalCount > 0 ? (activePage - 1) * perPage : 0;
    const limit = totalCount > perPage ? offset + 12 : totalCount;

    const changePage = (ev: any, page: number): void  => {
        dispatch(LoadInprogressStatsStartDelayed(page));
    }

    return (
        <Footer isMobile={isMobile}>
            <ModernPagination
                onChange={changePage}
                disabled={isLoading}
                count={Math.ceil(totalCount / 12)}
                siblingCount={1}
                boundaryCount={1}
                size="medium"
                page={activePage}
            />
            <SummaryContainer isMobile={isMobile}>
                {offset + 1} - {limit} {t('outof')} {totalCount} {t('items')}
            </SummaryContainer>
        </Footer>
    )
}
*/

const InprogressOverview: FC = (): JSX.Element => {
    const { t } = useTranslation();
    const isMobile = useMobile();

    return (
        <>
            <OverviewContainer isMobile={isMobile}>
                <Info isMobile={isMobile}>
                    <InfoIconContainer>
                        <InfoPointIcon />
                    </InfoIconContainer>
                    <Typography display="inline" bodyVariant="onPaper" variant="body1">
                        {t('inprogressestimates')}
                    </Typography>
                </Info>
                {isMobile ? <InProgressOverviewMobileTable /> : <InProgressOverviewDesktopTable />}
            </OverviewContainer>
        </>
    );
};

export default InprogressOverview;
